/**
 * Domain Context
 *
 * Provides domain detection and organization lookup based on the current domain.
 * This runs BEFORE auth to determine which org's branding to show on login screens.
 *
 * Priority order:
 * 1. Custom domains (portal.fitpro.com)
 * 2. Subdomains (fitpro.trainerpulse.app)
 * 3. Main app (trainerpulse.app)
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import { detectDomain, DomainInfo } from "@/lib/domain/detector";

interface DomainOrganization {
  id: string;
  name: string;
  branding: Record<string, unknown> | null;
  defaultRoute: string;
  subdomain: string | null;
}

interface DomainContextType {
  /** Current domain detection info */
  domainInfo: DomainInfo;
  /** Organization resolved from domain (null if main app or not found) */
  organization: DomainOrganization | null;
  /** Whether domain lookup is in progress */
  loading: boolean;
  /** Error if domain is invalid/unverified */
  error: string | null;
  /** Whether this is a white-label domain (subdomain or custom) */
  isWhiteLabel: boolean;
  /** Manually refresh the domain lookup */
  refresh: () => Promise<void>;
}

const DomainContext = createContext<DomainContextType | undefined>(undefined);

interface DomainProviderProps {
  children: ReactNode;
}

export function DomainProvider({ children }: DomainProviderProps) {
  // Detect domain once on mount
  const [domainInfo] = useState<DomainInfo>(() => detectDomain());
  const [organization, setOrganization] = useState<DomainOrganization | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(!domainInfo.isMainApp);
  const [error, setError] = useState<string | null>(null);

  const lookupOrganization = useCallback(async () => {
    // No lookup needed for main app
    if (domainInfo.isMainApp || !domainInfo.identifier) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call the database function to look up org by domain
      const { data, error: rpcError } = await supabase.rpc(
        "get_organization_by_domain",
        { p_domain: domainInfo.identifier }
      );

      if (rpcError) {
        console.error("[DomainContext] RPC error:", rpcError);
        throw new Error("Unable to verify domain");
      }

      if (!data || data.length === 0) {
        console.warn(
          "[DomainContext] Domain not found:",
          domainInfo.identifier
        );
        setError(
          "This domain is not configured. Please check your settings or contact support."
        );
        setOrganization(null);
      } else {
        const org = data[0];
        setOrganization({
          id: org.organization_id,
          name: org.organization_name,
          branding: org.branding,
          defaultRoute: org.default_route || "/portal",
          subdomain: org.subdomain,
        });
        setError(null);
      }
    } catch (err: unknown) {
      console.error("[DomainContext] Lookup failed:", err);
      const message = err instanceof Error ? err.message : "Unable to verify domain";
      setError(message);
      setOrganization(null);
    } finally {
      setLoading(false);
    }
  }, [domainInfo]);

  // Look up org on mount
  useEffect(() => {
    lookupOrganization();
  }, [lookupOrganization]);

  const refresh = useCallback(async () => {
    await lookupOrganization();
  }, [lookupOrganization]);

  const value: DomainContextType = {
    domainInfo,
    organization,
    loading,
    error,
    isWhiteLabel: !domainInfo.isMainApp,
    refresh,
  };

  return (
    <DomainContext.Provider value={value}>{children}</DomainContext.Provider>
  );
}

/**
 * Hook to access domain context
 */
export function useDomain() {
  const context = useContext(DomainContext);
  if (context === undefined) {
    throw new Error("useDomain must be used within a DomainProvider");
  }
  return context;
}

/**
 * Hook to check if on a white-label domain (can be used without provider)
 */
export function useIsWhiteLabel(): boolean {
  const context = useContext(DomainContext);
  if (context) {
    return context.isWhiteLabel;
  }
  // Fallback if not in provider
  return !detectDomain().isMainApp;
}
